import { useDynamicConfig } from '@statsig/react-bindings';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { reportMessage, zodIssueMessages } from '@/helpers/errors/report';

const PLATFORM_NOTIFICATION_KEY = 'platform_notification';

const notificationSchema = z.object({
  /** Renders a close button. The dismissal is remembered until the `message` changes. */
  dismissible: z.boolean().default(false),
  enabled: z.boolean(),
  /** Stable identifier, used as the React key and to namespace the dismissal in local storage. */
  id: z.string().min(1),
  linkHref: z.url().optional(),
  linkLabel: z.string().min(1).optional(),
  message: z.string().min(1),
  severity: z.enum(['info', 'success', 'warning', 'error']).default('info'),
  title: z.string().min(1).optional(),
});

export type PlatformNotification = z.infer<typeof notificationSchema>;

type ParsedNotifications = {
  /** Enabled, well-formed entries, in the order they are configured. */
  notifications: PlatformNotification[];
  /** Everything that could not be understood, for the caller to report. */
  issues: string[];
};

/**
 * Never throws and never reports: entries are validated one by one so a single broken
 * notification does not hide the others, and anything unusable degrades to an empty list
 * plus the issues that caused it.
 *
 * Reporting is left to the caller so the schema stays pure and testable on its own.
 */
export const platformNotificationSchema = z
  .object({ notifications: z.unknown() })
  .transform(({ notifications }): ParsedNotifications => {
    if (!Array.isArray(notifications)) {
      return { issues: ['`notifications` is missing or not an array'], notifications: [] };
    }

    const issues: string[] = [];
    const parsed = notifications.flatMap((entry, index) => {
      const result = notificationSchema.safeParse(entry);
      if (result.error) {
        issues.push(`notifications[${index}]: ${zodIssueMessages(result.error).join('; ')}`);
        return [];
      }

      return result.data.enabled ? [result.data] : [];
    });

    return { issues, notifications: parsed };
  })
  // The value is not an object at all.
  .catch(({ error }) => ({ issues: zodIssueMessages(error), notifications: [] }));

/** Stable identity, so the memo below does not invalidate on every render while nothing is configured. */
const EMPTY: ParsedNotifications = { issues: [], notifications: [] };

/**
 * Platform-wide notification banners, held in the `platform_notification` Statsig dynamic config.
 *
 * Everything about them (copy, severity, link, dismissibility, ordering) is edited in the Statsig
 * console, so announcing a maintenance window or any other notice never requires a deploy.
 */
export function usePlatformNotifications(): PlatformNotification[] {
  const value = useDynamicConfig(PLATFORM_NOTIFICATION_KEY).value;

  const { notifications, issues } = useMemo(
    // An absent config is the state while the Statsig client is still initializing, and whenever
    // nothing is configured. Neither is a misconfiguration, so neither is reported.
    () => (value && Object.keys(value).length > 0 ? platformNotificationSchema.parse(value) : EMPTY),
    [value],
  );

  // Reported from an effect rather than during render, so a config that stays broken is reported
  // once per Statsig value refresh instead of on every re-render.
  useEffect(() => {
    if (issues.length === 0) {
      return;
    }

    reportMessage('Invalid dynamic config', {
      domain: 'flags',
      level: 'warning',
      extra: { issues, key: PLATFORM_NOTIFICATION_KEY },
    });
  }, [issues]);

  return notifications;
}
